import { CanvasDataTypes } from './canvas-data-types';

type EditorOnlyFabricObject = fabric.Object & {
  data?: {
    removableOnSave?: boolean;
    type?: CanvasDataTypes | string;
  };
  getSrc?: () => string;
  src?: string;
};

const EDITOR_ONLY_CANVAS_TYPES = new Set<string>([
  CanvasDataTypes.PhotoZoneButton,
  CanvasDataTypes.UserZoneAddTextButton,
  CanvasDataTypes.UserZoneAddWamButton,
  CanvasDataTypes.UserZoneInstructions,
  CanvasDataTypes.FoldLine,
  CanvasDataTypes.EditableArea,
  CanvasDataTypes.StrokedMargin,
  CanvasDataTypes.PhotoIcon,
  CanvasDataTypes.PhotoTextZoneButton,
  CanvasDataTypes.PhotoTextZoneTextButton,
  CanvasDataTypes.PhotoTextZoneWAMButton,
  CanvasDataTypes.PhotoTextZoneResetButton,
]);

/**
 * Reads an image URL from either Fabric's image getter or raw src field.
 *
 * @param object Fabric image-like object
 * @returns Image URL, or an empty string when unavailable
 */
const getImageUrl = (object: EditorOnlyFabricObject): string => {
  if (typeof object.getSrc === 'function') return object.getSrc();
  return object.src ?? '';
};

/**
 * Checks whether a Fabric object is one of the editor warning/edit icons.
 *
 * @param object Fabric object from a canvas
 * @returns True when the object is an editor-only icon
 */
const isEditorIconObject = (object: EditorOnlyFabricObject): boolean => {
  if (object.type !== 'image') return false;
  const url = getImageUrl(object);
  return object.name === 'edit-icon' || url.includes('edit-icon') || url.includes('warning-icon');
};

/**
 * Checks whether a Fabric object exists only to support editor interactions.
 *
 * This centralizes the same concept used by save cleanup and quick preview:
 * objects marked `removableOnSave`, editable-area chrome, fold lines, editor
 * icons, and canvas buttons should not appear in generated preview imagery.
 *
 * @param object Fabric object from a canvas
 * @returns True when the object is editor-only chrome
 */
export const isEditorOnlyCanvasObject = (object: fabric.Object): boolean => {
  const editorObject = object as EditorOnlyFabricObject;
  const dataType = editorObject.data?.type;

  return (
    Boolean(editorObject.data?.removableOnSave) ||
    (typeof dataType === 'string' && EDITOR_ONLY_CANVAS_TYPES.has(dataType)) ||
    isEditorIconObject(editorObject)
  );
};
